import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { DomainApiProvider } from '@ui/shared/domain/DomainApiContext';
import { createDomainApi } from '@data/domain-api/create-domain-api';
import { createDexieEventCache } from '@data/cache/dexie-event-cache';
import { createSystemClockPort } from '@domain/clock/system-clock';
import { createUuidV7IdPort } from '@domain/id/uuidv7';
import { createHttpSyncTransport } from '@data/sync/http-sync-transport';
import { applyTheme, getStoredTheme } from '@ui/shared/tokens/theme';
import '@ui/shared/tokens/tokens.css';

/**
 * Composition root (ADR-0001 rule 5). Wires the production `DomainApi`
 * (dev-core-domain's src/domain + src/data, see src/data/domain-api/
 * create-domain-api.ts) to the Dexie cache, system clock, UUIDv7 IDs, and
 * the HTTP sync transport to the Worker (ADR-0004/0007). Everything above
 * this file (App, TodayView, ObjectiveDetailScreen) consumes only the
 * DomainApi interface from src/contracts and needed no changes when this
 * swapped from the earlier in-memory reference fixture
 * (src/ui/testing/realDomain.ts, still used by component tests per
 * ADR-0008's "real domain layer, never mocked" seam rule).
 *
 * Service-worker registration: manual (see vite.config.ts's top-of-file
 * note for why vite-plugin-pwa isn't used — a verified upstream build-time
 * crash under this project's Node version). vite.config.ts's
 * `build.rollupOptions.input` declares src/app/sw.ts as a second entry so
 * production emits a real dist/sw.js at the site root; Vite's own dev
 * server serves the same TS source directly at /src/app/sw.ts (transpiled
 * on the fly, same as any other module). Registration paths are plain
 * strings (not `new URL(..., import.meta.url)`) deliberately — that
 * expression form is Vite's asset-reference syntax and gets statically
 * detected and copied into the production bundle as a second, unused
 * artifact even inside a dev-only branch (verified: an earlier version of
 * this file produced exactly that dead file). Plain strings avoid the
 * false-positive asset detection entirely.
 *
 * Sync triggers (ADR-0004 "Pull ... Triggered: on app start, on
 * visibility/online"): this was the one trigger ADR-0004 names that had no
 * call site anywhere in src/app or src/ui — every other DomainApi consumer
 * only pushes as a side effect of a local append (create-domain-api.ts's
 * `append`), so a second device never pulled anything until some future
 * command happened to call `syncNow()`, which nothing did. Found while
 * building the first real-browser e2e coverage of the sync round-trip
 * journey (docs/adr/0008-testing-architecture.md addendum, tests/e2e/
 * sync-round-trip.spec.ts) — a second browser context loading the app
 * fresh never saw data pushed from the first, exactly the gap ADR-0004
 * already specifies the fix for. Wiring it up here is closing that gap
 * against the existing accepted decision, not a new product call.
 */

applyTheme(getStoredTheme());

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    const swUrl = import.meta.env.DEV ? '/src/app/sw.ts' : '/sw.js';
    navigator.serviceWorker.register(swUrl, { type: 'module' }).catch((error: unknown) => {
      // Installability/offline-shell is a fast-follow, not Phase 1 Must
      // (ADR-0007) — a registration failure must never block the app from
      // rendering, so this is swallowed after logging rather than thrown.
      console.error('Service worker registration failed:', error);
    });
  });
}

const domainApi = createDomainApi({
  cache: createDexieEventCache(),
  clock: createSystemClockPort(),
  id: createUuidV7IdPort(),
  transport: createHttpSyncTransport(),
});

// ADR-0004 sync triggers beyond "after every local append" (already handled
// inside create-domain-api.ts): app start, regaining connectivity, and the
// tab becoming visible again. `syncNow()` is push-then-pull and never
// throws past this boundary in normal operation (failures surface via
// `getSyncStatus()`, per ADR-0004's error table), so a rejection here is
// swallowed after logging rather than left as an unhandled rejection.
function triggerSync(): void {
  domainApi.syncNow().catch((error: unknown) => {
    console.error('Sync failed:', error);
  });
}

triggerSync();
window.addEventListener('online', triggerSync);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') triggerSync();
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}

createRoot(rootEl).render(
  <StrictMode>
    <DomainApiProvider api={domainApi}>
      <App />
    </DomainApiProvider>
  </StrictMode>,
);
